import { PageContainer } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Descriptions, Skeleton } from 'antd'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { OrderArchiveNotice } from './order-archive-notice.tsx'
import { OrderSectionHeading } from './order-detail-section.tsx'
import { OrderEnterprisePanel } from './order-enterprise-panel.tsx'
import { RentalDetailActions } from './rental-detail-actions.tsx'
import { RentalDetailSummary } from './rental-detail-summary.tsx'
import type { RentalListSearch } from './rental-list-search.ts'

export function RentalOrderDetails({ rentalOrderId, search }: { rentalOrderId: number; search: RentalListSearch }) {
  const trpc = useTRPC()
  const access = useAccess()
  const query = useQuery(trpc.order.rental.detail.queryOptions({ rentalOrderId }))
  const order = query.data
  return (
    <PageContainer breadcrumbRender={false} title={order?.orderNo ?? '租赁订单详情'}>
      {query.isPending && <Skeleton active />}
      {query.isError && (
        <Alert
          type="error"
          showIcon
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {order && (
        <div className="pb-28">
          <RentalDetailSummary order={order} />
          <OrderArchiveNotice archivedAt={order.archivedAt} />
          {order.status === 'pending' && (
            <Alert
              className="mt-4!"
              showIcon
              type="info"
              title="待处理：先保存合同，再核对首期费用"
              description="待处理租赁订单尚未初始化首期账单。保存合同后生成租金、押金等费用草稿；核费出账、确认生效与收款分别办理。"
            />
          )}
          <section className="border-border bg-surface mt-4 rounded-xl border p-5 shadow-xs sm:p-6">
            <OrderSectionHeading title="服务对象及资料" />
            <OrderEnterprisePanel
              subjectId={order.subjectId}
              subjectName={order.subjectName}
              currentOrder={{ type: 'rental', id: order.id }}
              canSupplement={access.can('order.rental.update') && !order.archivedAt && order.status !== 'archived'}
              onSuccess={() => void query.refetch()}
            />
          </section>

          <section className="border-border bg-surface mt-4 rounded-xl border p-5 shadow-xs sm:p-6">
            <OrderSectionHeading title="附件与备注" />

            <AttachmentUpload
              disabled
              value={order.attachments.map(({ file }) => file.id)}
              initialFiles={order.attachments.map(({ file }) => ({
                id: file.id,
                name: file.originName || file.fileName,
                fileType: file.fileType,
                size: Number(file.fileSize)
              }))}
            />
            <div className={'h-4'} />
            <Descriptions
              className={'mb-3'}
              column={1}
              items={[
                {
                  key: 'note',
                  label: '订单备注',
                  children: <span className="wrap-break-word whitespace-pre-wrap">{order.note || '—'}</span>
                }
              ]}
            />
          </section>

          <RentalDetailActions order={order} search={search} />
        </div>
      )}
    </PageContainer>
  )
}
